import {
  buildLaikaChatRequestBody,
  type LaikaChatUserFields,
  type LaikaSidebarNavHints,
} from '../../utils/buildLaikaChatUserContext';
import type { LaikaPageContext } from '../../utils/buildLaikaPageContext';
import type { ActionButton } from './types';
import { consumeLaikaSseResponse } from './consumeLaikaSse';
import { laikaStreamUrl } from './laikaStreamUrl';
import { normalizeActionButtons } from './laikaActionClick';
import { resolveLaikaChatUrl } from './assistantAgent';

export type AssistantReply = {
  text: string;
  conversationId: string | null;
  ms: number;
  actions?: ActionButton[];
  sources?: string[];
  confidence?: number;
  error?: string;
};

/** JSON fallback only when the stream route is missing / not implemented. */
export function shouldRetryLaikaJsonAfterStreamStatus(status: number): boolean {
  return status === 404 || status === 405 || status === 501;
}

/** Keep JSON fallback for network / missing stream; not after a real stream failure. */
export function shouldSwallowStreamErrorForJsonRetry(
  err: unknown,
  streamCommitted: boolean,
): boolean {
  if (streamCommitted) return false;
  const name = (err as { name?: string } | null)?.name;
  if (name === 'AbortError') return false;
  const msg = String((err as { message?: string } | null)?.message || '');
  const status = Number((msg.match(/^(\d{3})\b/) || [])[1]);
  if (status && !shouldRetryLaikaJsonAfterStreamStatus(status)) return false;
  return true;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token') || 'test';
  return {
    'Content-Type': 'application/json',
    ...(token && token !== 'test' ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function fetchLaikaReply(params: {
  message: string;
  conversationId: string | null;
  laikaDataAccessEnabled: boolean;
  user?: LaikaChatUserFields | null;
  pageContext: LaikaPageContext;
  sidebarNav?: LaikaSidebarNavHints;
  fallbackText: string;
  signal?: AbortSignal;
  onStatus?: (status: string | null) => void;
  onPartial?: (text: string) => void;
}): Promise<AssistantReply> {
  const started = performance.now();
  const url = resolveLaikaChatUrl();
  const streamUrl = laikaStreamUrl(url);
  const body = buildLaikaChatRequestBody({
    message: params.message,
    conversationId: params.conversationId,
    laikaDataAccessEnabled: params.laikaDataAccessEnabled,
    user: params.user,
    pageContext: params.pageContext,
    sidebarNav: params.sidebarNav,
  });
  const headers = authHeaders();

  try {
    let streamCommitted = false;
    try {
      const streamResponse = await fetch(streamUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: params.signal,
      });
      if (
        streamResponse.ok &&
        (streamResponse.headers.get('content-type') || '').includes('text/event-stream')
      ) {
        streamCommitted = true;
        const streamed = await consumeLaikaSseResponse(
          streamResponse,
          (status) => {
            params.onStatus?.(status);
          },
          (text) => {
            params.onPartial?.(text);
          },
        );
        return {
          text: streamed.response || params.fallbackText,
          conversationId: streamed.conversationId,
          ms: Math.round(performance.now() - started),
          actions: streamed.actions.length > 0 ? streamed.actions : undefined,
          sources: streamed.sources.length > 0 ? streamed.sources : undefined,
        };
      }
      if (streamResponse.ok) {
        const data = await streamResponse.json();
        return {
          text: data.response || data.message || data.answer || params.fallbackText,
          conversationId: data.conversation_id || null,
          ms: Math.round(performance.now() - started),
          actions: normalizeActionButtons(data.actions),
          sources: data.sources,
          confidence: data.confidence,
        };
      }
      if (!shouldRetryLaikaJsonAfterStreamStatus(streamResponse.status)) {
        throw new Error(`${streamResponse.status} ${streamResponse.statusText}`);
      }
    } catch (streamErr) {
      if (!shouldSwallowStreamErrorForJsonRetry(streamErr, streamCommitted)) {
        throw streamErr;
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: params.signal,
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return {
      text: data.response || data.message || data.answer || params.fallbackText,
      conversationId: data.conversation_id || null,
      ms: Math.round(performance.now() - started),
      actions: normalizeActionButtons(data.actions),
      sources: data.sources,
      confidence: data.confidence,
    };
  } catch (error: any) {
    return {
      text: '',
      conversationId: null,
      ms: Math.round(performance.now() - started),
      error: error?.name === 'AbortError' ? 'timeout' : error?.message || 'error',
    };
  }
}
